import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import type { WaveformShape } from 'fym-shared/src/music24/assetStyles';
import type { StudioDetails } from '../studio/studioRules';
import type { ReelDurationSec, ReelVoiceId } from '../types';

/**
 * The way back after a credit-pack purchase (owner rule 2026-10-06: after the purchase the user
 * is on the same Studio step with the same draft and can make it at once).
 *
 * The Stripe Checkout leaves the app, so the in-memory draft is lost. Before the redirect,
 * `saveCreditPackReturn` writes the page address and the draft on screen to `sessionStorage` (the
 * tab only). The payment page reads the address and goes back to it; the Studio and the AI reel
 * form take their draft once. The storage can be missing or throw (a private window): then the
 * user goes back with an empty draft.
 */

const STORAGE_KEY = 'm24CreditPackReturn';
/** A return older than this is stale: the user left the checkout and came back much later. */
const MAX_AGE_MS = 3 * 60 * 60 * 1000;

/** The step-3 draft of a kit asset type in the Studio. */
export interface StudioReturnDraft {
  catalogItemUuid: string;
  themeId: StudioThemeId;
  edits: StudioDetails;
}

/** The AI reel form. */
export interface ReelReturnDraft {
  catalogItemUuid: string;
  lyrics: string;
  waveform?: WaveformShape;
  voiceId: ReelVoiceId | null;
  durationSec: ReelDurationSec;
  instructions?: string;
}

interface CreditPackReturn {
  returnUrl: string;
  savedAt: number;
  studio?: StudioReturnDraft;
  reel?: ReelReturnDraft;
}

type DraftSource<T> = () => T | null;

let studioSource: DraftSource<StudioReturnDraft> | null = null;
let reelSource: DraftSource<ReelReturnDraft> | null = null;

/** The Studio tells which step-3 draft is on screen. Returns the function that removes it. */
export function registerStudioReturnDraft(source: DraftSource<StudioReturnDraft>): () => void {
  studioSource = source;
  return () => {
    if (studioSource === source) {
      studioSource = null;
    }
  };
}

/** The AI reel form tells which values are on screen. Returns the function that removes it. */
export function registerReelReturnDraft(source: DraftSource<ReelReturnDraft>): () => void {
  reelSource = source;
  return () => {
    if (reelSource === source) {
      reelSource = null;
    }
  };
}

function read(): CreditPackReturn | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const value = JSON.parse(raw) as CreditPackReturn;
    if (typeof value.returnUrl !== 'string' || typeof value.savedAt !== 'number') {
      return null;
    }
    if (Date.now() - value.savedAt > MAX_AGE_MS) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

function write(value: CreditPackReturn | null): void {
  try {
    if (value === null || (value.studio === undefined && value.reel === undefined && value.returnUrl === '')) {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // No storage: the user goes back with an empty draft.
  }
}

/** Called just before the Stripe redirect of a credit pack. */
export function saveCreditPackReturn(returnUrl: string): void {
  write({
    returnUrl,
    savedAt: Date.now(),
    studio: studioSource?.() ?? undefined,
    reel: reelSource?.() ?? undefined,
  });
}

/**
 * True for an address of the web app itself: the same origin, at or under `/app`. Any other
 * address is never a way back (an open redirect).
 */
export function isAppReturnUrl(url: string, origin: string): boolean {
  try {
    const parsed = new URL(url);
    if (parsed.origin !== origin) {
      return false;
    }
    return parsed.pathname === '/app' || parsed.pathname.startsWith('/app/');
  } catch {
    return false;
  }
}

/**
 * The page to go back to after the purchase, once. The drafts stay for the Studio and the reel
 * form. Null when there is none or when the address is not a page of the app.
 */
export function takeCreditPackReturnUrl(origin: string): string | null {
  const value = read();
  if (value === null || value.returnUrl === '') {
    return null;
  }
  write({ ...value, returnUrl: '' });
  return isAppReturnUrl(value.returnUrl, origin) ? value.returnUrl : null;
}

/** A full load of the page to go back to: it reads the new balance fresh and restores the draft. */
export function navigateToReturnUrl(url: string): void {
  window.location.replace(url);
}

/** The step-3 draft of the song and the asset type, once. */
export function takeStudioReturnEdits(
  catalogItemUuid: string | undefined,
  themeId: StudioThemeId | undefined
): StudioDetails | null {
  const value = read();
  const studio = value?.studio;
  if (value === null || studio === undefined) {
    return null;
  }
  if (studio.catalogItemUuid !== catalogItemUuid || studio.themeId !== themeId) {
    return null;
  }
  write({ ...value, studio: undefined });
  return studio.edits;
}

/**
 * The AI reel form of the song. It does not remove the draft, because a state initializer may run
 * twice (React strict mode); the form calls `clearReelReturnDraft` after its first render.
 */
export function peekReelReturnDraft(catalogItemUuid: string | undefined): ReelReturnDraft | null {
  const reel = read()?.reel;
  if (reel === undefined || reel.catalogItemUuid !== catalogItemUuid) {
    return null;
  }
  return reel;
}

/** Removes the AI reel form draft, so a later visit starts empty. */
export function clearReelReturnDraft(): void {
  const value = read();
  if (value !== null && value.reel !== undefined) {
    write({ ...value, reel: undefined });
  }
}
